import CTNButton from '@ctn/ui/components/ctn-button';
import CTNTabNavigation from '@ctn/ui/components/ctn-tab-navigation';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import { useDirtyForm } from '@/context/dirtyForm.context';
import { EmpresaProvider } from '@/context/empresa.context';
import { usePageContext } from '@/context/page.context';
import UsuariosListado from '@/pages/Administrar/UsuariosListado';

import { type IUserListado } from '@/interfaces/model/user.interface';

import Empresa from './Empresa';
import { ROUTES, ROUTES_LABEL } from '../../constants/routes.constants';

const TAB_ID = {
	datosEmpresa: 'datosEmpresa',
	usuarios: 'usuarios',
};

const Administrar = (): ReactElement => {
	const { initBreadcrumb } = usePageContext();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [selectedId, setSelectedId] = useState<string>(state?.activeTab ?? TAB_ID.datosEmpresa);
	const { isDirtyForm, checkDirtyForm, setIsDirtyForm } = useDirtyForm();

	const onAdd = () => navigate(ROUTES.USER_NEW);

	const onEdit = (rowData: IUserListado) => {
		const { id } = rowData;
		return navigate(`${ROUTES.USER}/${id}`);
	};

	useEffect(() => {
		initBreadcrumb(ROUTES_LABEL.ADMINISTRAR);
	}, [state]);

	const tabs = [
		{
			key: TAB_ID.datosEmpresa,
			header: 'Datos de la empresa',
			content: (
				<EmpresaProvider>
					<Empresa />
				</EmpresaProvider>
			),
		},
		{
			key: TAB_ID.usuarios,
			header: 'Usuarios',
			content: (
				<div className="[ u-flow p-admin__user-list ]">
					<CTNButton label="Añadir usuario" size="size-40" className="p-admin__add" onClick={onAdd} />
					<UsuariosListado onEdit={onEdit} />
				</div>
			),
		},
	];

	const selectedTab = useMemo(() => {
		// @ts-expect-error pass
		const tabByUrl = tabs.find((t) => t.assignUrl === location.pathname);
		if (tabByUrl) return tabByUrl;

		return tabs.find((t) => t.key === selectedId) ?? tabs[0];
	}, [location.pathname, selectedId, tabs]);

	const handleChange = (index: string) => {
		if (isDirtyForm) {
			checkDirtyForm({
				action() {
					setIsDirtyForm(false);
					setSelectedId(index);
				},
			});
		} else {			
			setSelectedId(index);			
		}
	};

	return (
		<div className="p-admin">
			<PageHeader hasHelp title="Administrar" className="u-mt-8" />
			<CTNTabNavigation
				className="u-mt-24"
				autoAction={false}
				activeTab={selectedId}
				tabs={tabs}
				onTabChange={({ index }) => handleChange(index)}
			/>
			<div className="[ tab-content ] [ u-mt-24 ]">{selectedTab?.content}</div>
		</div>
	);
};

export default Administrar;
